import React, { useState, useRef } from "react"
import styled, { css } from "styled-components"
import { useStaticQuery, graphql } from "gatsby"
import { useIntl } from "gatsby-plugin-react-intl"
import { useClickAway, useKeyPressEvent } from "react-use"

import StyledLink from "./Link"
import StyledNavigationGrid, { SubNavStyles } from "./NavigationGrid"

import { media } from "../styles"
import ArrowRightSVG from "../images/icons/arrow-right.inline.svg"

const menuStyles = css`
  font-size: var(--font-size-15);
  font-weight: 700;
  letter-spacing: -0.07px;
`

const menuServicesOffsetLg = "25.5%"
const menuServicesOffsetXl = "22.9%"
const menuIndustriesOffsetLg = "34.5%"
const menuIndustriesOffsetXl = "32.9%"

const linkStyles = css`
  border-radius: 3px;
  font-weight: 700 !important;
  padding: 8px;
  transition:
    color var(--transition-time),
    background var(--transition-time);
  width: 100%;

  &:focus {
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  .link-icon {
    flex: none;
    height: auto;
    margin: 0 5px 0 auto;
    opacity: 0;
    transition: opacity var(--transition-time);
    width: 10px;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }

  &:hover,
  &:focus {
    background: var(--color-grey-light);
    color: var(--color-spaceblue);

    .link-icon {
      opacity: 1;
    }
  }

  > span {
    width: 100%;
  }

  .link-text {
    align-items: center;
    display: flex;
    gap: 11px;
    width: 100%;
  }
`

const NavContainer = styled.nav`
  display: none;
  position: relative;
  z-index: 99999;

  @media ${media.lg} {
    display: block;
  }

  @media print {
    display: none;
  }
`

const Flyout = styled.div`
  display: inline;
  left: 50%;
  opacity: 0;
  pointer-events: none;
  position: absolute;
  top: calc(100% + 25px);
  transform-origin: top center;
  transform: translate3d(-50%, -10px, 0) scale(0.94);
  transition:
    opacity var(--transition-time),
    transform var(--transition-time);
  z-index: 1;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  ${(props) =>
    props.$isOpen &&
    css`
      opacity: 1;
      pointer-events: auto;
      transform: translate3d(-50%, 0, 0) scale(1);
    `}

  ${(props) =>
    props.$category === "services" &&
    css`
      transform: ${`translate3d(-${menuServicesOffsetLg}, -10px, 0) scale(0.94)`};
      transform-origin: ${menuServicesOffsetLg} 0;

      ${(props) =>
        props.$isOpen &&
        css`
          transform: ${`translate3d(-${menuServicesOffsetLg}, 0, 0) scale(1)`};
        `}

      @media ${media.xl} {
        transform: ${`translate3d(-${menuServicesOffsetXl}, -10px, 0) scale(0.94)`};
        transform-origin: ${menuServicesOffsetXl} 0;

        ${(props) =>
          props.$isOpen &&
          css`
            transform: ${`translate3d(-${menuServicesOffsetXl}, 0, 0) scale(1)`};
          `}
      }
    `}

    ${(props) =>
    props.$category === "industries" &&
    css`
      transform: ${`translate3d(-${menuIndustriesOffsetLg}, -10px, 0) scale(0.94)`};
      transform-origin: ${menuIndustriesOffsetLg} 0;

      ${(props) =>
        props.$isOpen &&
        css`
          transform: ${`translate3d(-${menuIndustriesOffsetLg}, 0, 0) scale(1)`};
        `}

      @media ${media.xl} {
        transform: ${`translate3d(-${menuIndustriesOffsetXl}, -10px, 0) scale(0.94)`};
        transform-origin: ${menuIndustriesOffsetXl} 0;

        ${(props) =>
          props.$isOpen &&
          css`
            transform: ${`translate3d(-${menuIndustriesOffsetXl}, 0, 0) scale(1)`};
          `}
      }
    `}
`

const Menu = styled.ul`
  ${menuStyles}
  display: flex;
  flex-flow: row nowrap;
  gap: 10px;
  list-style: none;
  padding: 0;

  @media ${media.xl} {
    gap: 20px;
  }

  a,
  button {
    align-items: center;
    color: inherit;
    display: flex;
    font-size: inherit;
    font-weight: inherit;
    text-decoration: none;
    transition: color var(--transition-time);
    padding: 5px;

    &.sublink {
      ${linkStyles}
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }

    svg {
      flex: none;
    }

    &:hover,
    &:focus {
      color: var(--color-neutral-dark);
    }
  }
`

const MenuItem = styled.li`
  margin: 0;
  padding: 0;
  position: relative;

  &:last-child {
    border: none;
  }
`

const Link = styled(StyledLink)`
  ul ul &.current {
    color: var(--color-neutral-dark);
    font-weight: 700;
  }
`

const SubmenuToggle = styled.button`
  appearance: none;
  background: transparent;
  border: none;
  cursor: default;
  line-height: inherit;
  margin: 0;
  padding: 0;

  &:hover,
  &:focus {
    color: var(--color-neutral-dark);
  }

  ${(props) =>
    props.$isOpen &&
    css`
      color: var(--color-neutral-dark) !important;
    `}
`

const SubMenu = styled.ul`
  ${SubNavStyles}
  display: flex;
  flex-flow: column nowrap;
  gap: 2px;
  list-style: none;
  margin: 0 -8px;
  padding: 0;
  width: calc(100% + 16px);

  ${(props) =>
    props.$isOpen &&
    css`
      color: var(--color-neutral-dark) !important;
    `}

  a,
  button {
    padding: 2px 0;

    &:hover,
    &:focus {
      color: var(--color-neutral-dark);
    }
  }
`

const SubContainer = styled.div`
  background: var(--color-white);
  border-radius: 18px;
  box-shadow: 0 0 45px rgba(var(--color-spaceblue-rgb), 0.1);
  padding: 30px;
  position: relative;
  min-width: 245px;

  &::before {
    border: 20px solid transparent;
    border-bottom-color: #fff;
    content: "";
    display: block;
    height: 0;
    left: 50%;
    margin: 0 auto;
    pointer-events: none;
    position: absolute;
    top: -40px;
    transform: translateX(-50%);
    width: 0;

    ${(props) =>
      props.$category === "services" &&
      css`
        left: ${menuServicesOffsetLg};

        @media ${media.xl} {
          left: ${menuServicesOffsetXl};
        }
      `}

    ${(props) =>
      props.$category === "industries" &&
      css`
        left: ${menuIndustriesOffsetLg};

        @media ${media.xl} {
          left: ${menuIndustriesOffsetXl};
        }
      `}
  }
`

const NavigationGrid = styled(StyledNavigationGrid)`
  max-width: 100%;

  a,
  button {
    ${linkStyles}
  }

  @media ${media.lg} {
    max-width: none;
    width: 950px;
  }

  @media ${media.xl} {
    width: 950px;
  }
`

const SubNav = ({
  category,
  id,
  index,
  isOpen,
  mode,
  children,
  onClickAway,
  parentButtonRef,
}) => {
  const ref = useRef(null)

  useClickAway(ref, () => {
    isOpen && onClickAway()
  })

  return (
    <Flyout
      aria-labelledby={`desktop-menu-item-${index}`}
      id={id}
      $isOpen={isOpen}
      $mode={mode}
      $category={category}
      onMouseLeave={() => onClickAway()}
      onBlur={(event) => {
        // Check if focus is moving outside the submenu
        if (
          event.relatedTarget &&
          !event.currentTarget.contains(event.relatedTarget)
        ) {
          const currentTarget = event.currentTarget

          setTimeout(() => {
            const activeElement = document.activeElement
            // Don't close if focus moved to the parent button or is still within submenu
            if (
              currentTarget &&
              !currentTarget.contains(activeElement) &&
              activeElement !== parentButtonRef.current
            ) {
              onClickAway()
            }
          }, 0)
        }
      }}
      ref={ref}
      inert={!isOpen ? "" : undefined}
    >
      {mode === "grid" ? (
        <SubContainer $mode={mode} $category={category}>
          {children}
        </SubContainer>
      ) : (
        <SubContainer $mode={mode}>
          <SubMenu>{children}</SubMenu>
        </SubContainer>
      )}
    </Flyout>
  )
}

const HeaderNavigationDesktop = (props) => {
  const [subOpen, setSubOpen] = useState()
  const intl = useIntl()
  const menuItemRefs = useRef({})
  const navContainerRef = useRef(null)

  useKeyPressEvent("Escape", () => {
    if (subOpen) {
      // Find the parent button for the currently open submenu
      const openIndex = navItems?.findIndex((item) => item.slug === subOpen)
      if (openIndex !== -1 && menuItemRefs.current[openIndex]?.current) {
        // Return focus to the parent button
        menuItemRefs.current[openIndex].current.focus()
      }
      toggleSubMenu({ path: null })
    }
  })

  const close = () => {
    setSubOpen()
  }

  const toggleSubMenu = ({ path }) => {
    setSubOpen(path || null)
  }

  const data = useStaticQuery(graphql`
    query HeaderNavigationDesktopQuery {
      allNavigationJson {
        nodes {
          locale
          items {
            category
            label
            slug
            mobileOnly
            # url
            mode
            items {
              label
              slug
              url
            }
          }
        }
      }
      allProductCollections: allContentfulProductCollection(
        filter: { visible: { eq: true }, isOld: { ne: true } }
        sort: { sortOrder: ASC }
      ) {
        nodes {
          id
          node_locale
          name
          items: products {
            ... on ContentfulProduct {
              name
              slug
              description: navigationDescription
            }
          }
        }
      }
      allIndustryCollections: allContentfulIndustryCollection(
        sort: { sortOrder: ASC }
      ) {
        nodes {
          id
          name
          description: navigationDescription
          node_locale
          items: industries {
            name
            slug
            hasIndustryPage
            hideIndustryPageFromNavigation
          }
        }
      }
    }
  `)

  const navItems = data.allNavigationJson.nodes
    .find((node) => node.locale === intl.locale)
    ?.items?.filter((navItem) => navItem.mobileOnly !== "true")

  const productCollections = data.allProductCollections.nodes.filter(
    (node) => node.node_locale === intl.locale
  )

  const industryCollections = data.allIndustryCollections.nodes.filter(
    (node) => node.node_locale === intl.locale
  )

  const renderMenuItem = ({
    index,
    isFirst,
    isLast,
    isToplevel,
    item,
    prefix,
    subOpen,
  }) => {
    // Create ref for this menu item if it doesn't exist
    if (!menuItemRefs.current[index]) {
      menuItemRefs.current[index] = React.createRef()
    }

    const handleBlur = (event) => {
      if (event.relatedTarget) {
        const relatedTarget = event.relatedTarget

        // Check if focus moved to another top-level navigation item
        const isTopLevelNavItem =
          relatedTarget.id && relatedTarget.id.startsWith("desktop-menu-item-")

        // Check if focus is still within the navigation container
        const isFocusWithinNav =
          navContainerRef.current &&
          navContainerRef.current.contains(relatedTarget)

        if (isTopLevelNavItem) {
          // Focus moved to another top-level nav item, close current submenu
          toggleSubMenu({ path: null })
        } else if (!isFocusWithinNav) {
          // Focus moved outside the navigation area entirely, close submenu
          toggleSubMenu({ path: null })
        }
      }
    }
    if (item.items?.length > 0) {
      return (
        <SubmenuToggle
          aria-controls={`desktop-submenu-${index}`}
          aria-haspopup="true"
          id={`desktop-menu-item-${index}`}
          $isOpen={subOpen === item.slug}
          onBlur={handleBlur}
          onClick={(e) => {
            e.preventDefault()
            toggleSubMenu({ path: subOpen === item.slug ? null : item.slug })
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault()
              toggleSubMenu({ path: subOpen === item.slug ? null : item.slug })
            }
          }}
          // We don't want to have an onFocus event here
          // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
          onMouseOver={() => toggleSubMenu({ path: item.slug })}
          ref={menuItemRefs.current[index]}
          type="button"
        >
          {item.label}
        </SubmenuToggle>
      )
    } else if (item.slug) {
      const slug = prefix ? `${prefix}/${item.slug}/` : item.slug
      return (
        <Link
          arrow={false}
          className={!isToplevel ? "sublink" : ""}
          intl={item.intl === false}
          onBlur={isFirst || isLast ? handleBlur : null}
          // We don't want to have an onFocus event here
          // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
          onMouseOver={isToplevel ? () => toggleSubMenu({ path: null }) : null}
          to={slug}
        >
          <span className="link-text">
            {item.label}
            {!isToplevel && (
              <ArrowRightSVG className="link-icon" aria-hidden="true" />
            )}
          </span>
        </Link>
      )
    } else if (item.url) {
      return (
        <Link
          arrow={false}
          className={!isToplevel ? "sublink" : ""}
          onBlur={isFirst || isLast ? handleBlur : null}
          // We don't want to have an onFocus event here
          // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
          onMouseOver={isToplevel ? () => toggleSubMenu({ path: null }) : null}
          to={item.url}
        >
          <span className="link-text">
            {item.label}
            {!isToplevel && (
              <ArrowRightSVG className="link-icon" aria-hidden="true" />
            )}
          </span>
        </Link>
      )
    } else {
      return item.label
    }
  }

  return (
    <NavContainer
      {...props}
      aria-label={intl.formatMessage({
        id: "accessibility.regions.mainNavigation",
      })}
      role="navigation"
      ref={navContainerRef}
    >
      <Menu>
        {navItems.map((navItem, topIndex) => (
          <MenuItem key={navItem.label}>
            {renderMenuItem({
              item: navItem,
              subOpen,
              isToplevel: true,
              isFirst: topIndex === 0,
              isLast: topIndex === navItems.length - 1,
              index: topIndex,
            })}
            {navItem.items && (
              <SubNav
                category={navItem.category}
                id={`desktop-submenu-${topIndex}`}
                index={topIndex}
                isOpen={subOpen === navItem.slug}
                mode={navItem.mode}
                onClickAway={() => close()}
                parentButtonRef={menuItemRefs.current[topIndex]}
              >
                {navItem.items.map((subItem, subIndex) => {
                  switch (subItem.label) {
                    case "RENDER_SERVICES":
                      return (
                        <NavigationGrid
                          basePath="/services/"
                          columns={3}
                          headerLevel={2}
                          items={productCollections}
                          key={subItem.label}
                          mode="topnav"
                        />
                      )
                    case "RENDER_INDUSTRIES":
                      return (
                        <NavigationGrid
                          basePath="/industries/"
                          columns={4}
                          headerLevel={2}
                          items={industryCollections}
                          key={subItem.label}
                          mode="topnav"
                        />
                      )
                    default:
                      return (
                        <MenuItem key={subItem.label}>
                          {renderMenuItem({
                            item: subItem,
                            subOpen,
                            parent: navItem.slug,
                            isLast:
                              topIndex === navItems.length - 1 &&
                              subIndex === navItem.items.length - 1,
                          })}
                        </MenuItem>
                      )
                  }
                })}
              </SubNav>
            )}
          </MenuItem>
        ))}
      </Menu>

      {/* {subOpen && <Overlay onClick={() => close()} />} */}
    </NavContainer>
  )
}

export default HeaderNavigationDesktop
